import React, { useState } from 'react';
import { FaSpinner, FaUserShield, FaUnlink, FaUserTie } from 'react-icons/fa';
import MappingModal from './MappingModal';

/**
 * Advisors tab — Super Admin can:
 *   • See every advisor with their current manager mapping
 *   • Click "Map" to change/assign a manager
 *   • Activate / deactivate the advisor
 */
const AdminAdvisorsTab = ({ advisors, managers = [], handleToggleAdvisorStatus, onMapManager }) => {
  const [mapping, setMapping] = useState(null); // { advisor, currentManagerId } | null

  const closeModal = () => setMapping(null);

  const options = managers
    .filter(m => m.is_active !== false)
    .map(m => ({ id: m.id, name: m.name, subtitle: m.email || m.company_name }));

  return (
    <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]" data-testid="admin-advisors-tab">
      <h3 className="text-2xl font-bold text-white mb-6">All Advisors ({advisors?.length || 0})</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 border-b border-[#2D5A8F]">
              <th className="px-4 py-3 text-sm">Name</th>
              <th className="px-4 py-3 text-sm">Email</th>
              <th className="px-4 py-3 text-sm">Mapped Manager</th>
              <th className="px-4 py-3 text-sm">Status</th>
              <th className="px-4 py-3 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {advisors?.length > 0 ? advisors.map(advisor => {
              const mgr = managers.find(m => m.id === advisor.manager_id);
              return (
                <tr key={advisor.id} className="border-b border-[#2D5A8F]/50 hover:bg-[#1E3A5F]/40">
                  <td className="px-4 py-3 text-white">{advisor.name}</td>
                  <td className="px-4 py-3 text-gray-300 text-sm">{advisor.email}</td>
                  <td className="px-4 py-3 text-sm" data-testid={`advisor-mapped-mgr-${advisor.id}`}>
                    {mgr ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-200 border border-purple-500/30 text-xs">
                        <FaUserTie size={10} /> {mgr.name}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic text-xs">Independent</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${advisor.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {advisor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setMapping({ advisor, currentManagerId: advisor.manager_id || null })}
                        data-testid={`open-map-manager-${advisor.id}`}
                        className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/40 text-purple-200 rounded-md text-xs font-medium hover:bg-purple-500/25 transition-all flex items-center gap-1.5"
                      >
                        <FaUserShield size={11} /> {advisor.manager_id ? 'Re-Map' : 'Map'}
                      </button>
                      <button
                        onClick={() => handleToggleAdvisorStatus(advisor)}
                        className="px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 rounded text-xs hover:bg-yellow-500/30"
                      >
                        {advisor.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan="5" className="text-center py-8 text-gray-500"><FaSpinner className="animate-spin inline" /> Loading...</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mapping modal */}
      {mapping && (
        <MappingModal
          isOpen={!!mapping}
          onClose={closeModal}
          title="Map Advisor to Manager"
          subjectLabel="Advisor"
          subjectName={mapping.advisor.name}
          targetLabel="Manager"
          options={options}
          currentTargetId={mapping.currentManagerId}
          onSubmit={(managerId) => onMapManager(mapping.advisor.id, managerId)}
        />
      )}
    </div>
  );
};

export default AdminAdvisorsTab;
